import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface ClientProject {
  id: string;
  project_code: string;
  project_name: string;
  client_name: string | null;
  site_address: string | null;
  status: string;
  summary: string | null;
  start_date: string | null;
  target_completion_date: string | null;
  cover_image: string | null;
  project_type: string | null;
  updated_at: string;
}

type ClientProjectRow = ClientProject & { is_archived?: boolean | null };

const PROJECT_SELECT =
  'id, project_code, project_name, client_name, site_address, status, summary, start_date, target_completion_date, cover_image, project_type, updated_at';

interface UseClientProjectResult {
  projects: ClientProject[];
  selectedProject: ClientProject | null;
  projectId: string | null;
  projectCode: string | null;
  loading: boolean;
  error: string | null;
  selectProject: (project: ClientProject) => void;
}

function mergeClientProjects(rows: ClientProjectRow[]): ClientProject[] {
  const byId = new Map<string, ClientProjectRow>();
  for (const project of rows) {
    if (!byId.has(project.id)) byId.set(project.id, project);
  }

  let merged = Array.from(byId.values());
  if (merged.some((p) => Object.prototype.hasOwnProperty.call(p, 'is_archived'))) {
    merged = merged.filter((p) => p.is_archived !== true);
  }

  merged.sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );

  return merged.map(({ is_archived: _archived, ...project }) => project);
}

export function useClientProject(): UseClientProjectResult {
  const [projects, setProjects] = useState<ClientProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<ClientProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    // eslint-disable-next-line no-console
    console.time('client-project-load');

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // eslint-disable-next-line no-console
      console.log('[useClientProject] current user:', { email: user?.email, id: user?.id });

      if (!user?.id) {
        setError('Not signed in');
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      const { data: profile, error: profileErr } = await supabase
        .from('jphph_profiles')
        .select('id, email, user_type, is_active')
        .eq('id', user.id)
        .maybeSingle();

      // eslint-disable-next-line no-console
      console.log('[useClientProject] profile:', {
        id: profile?.id,
        email: profile?.email,
        user_type: profile?.user_type,
        is_active: profile?.is_active,
        error: profileErr?.message,
      });

      if (profileErr || !profile) {
        setError('Profile not found');
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      if ((profile.user_type ?? '').toLowerCase() !== 'client') {
        setError('Not a client account');
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      if (!profile.is_active) {
        setError('Account is inactive');
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      const { data: directProjects, error: directErr } = await supabase
        .from('jphph_projects')
        .select(PROJECT_SELECT)
        .eq('client_user_id', profile.id);

      // eslint-disable-next-line no-console
      console.log('[useClientProject] direct projects:', {
        count: directProjects?.length ?? 0,
        error: directErr?.message,
      });

      if (directErr) {
        setError(directErr.message);
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      const { data: memberRows, error: memberErr } = await supabase
        .from('jphph_project_members')
        .select('project_id')
        .eq('user_id', profile.id)
        .eq('member_role', 'client');

      // eslint-disable-next-line no-console
      console.log('[useClientProject] client member rows:', {
        count: memberRows?.length ?? 0,
        error: memberErr?.message,
      });

      if (memberErr) {
        setError(memberErr.message);
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      const directIds = new Set((directProjects || []).map((p) => p.id as string));
      const memberProjectIds = Array.from(
        new Set(
          (memberRows || [])
            .map((row) => row.project_id as string)
            .filter((projectId) => projectId && !directIds.has(projectId)),
        ),
      );

      let memberProjects: ClientProjectRow[] = [];
      if (memberProjectIds.length > 0) {
        const { data: memberProjectRows, error: memberProjectsErr } = await supabase
          .from('jphph_projects')
          .select(PROJECT_SELECT)
          .in('id', memberProjectIds);

        // eslint-disable-next-line no-console
        console.log('[useClientProject] member projects:', {
          count: memberProjectRows?.length ?? 0,
          error: memberProjectsErr?.message,
        });

        if (memberProjectsErr) {
          setError(memberProjectsErr.message);
          setProjects([]);
          setSelectedProject(null);
          return;
        }

        memberProjects = (memberProjectRows || []) as ClientProjectRow[];
      }

      const realProjects = mergeClientProjects([
        ...((directProjects || []) as ClientProjectRow[]),
        ...memberProjects,
      ]);

      setProjects(realProjects);

      if (realProjects.length > 0) {
        setSelectedProject(realProjects[0]);
        // eslint-disable-next-line no-console
        console.log('[useClientProject] selected project:', {
          id: realProjects[0].id,
          name: realProjects[0].project_name,
        });
      } else {
        setSelectedProject(null);
      }
    } catch (e) {
      setError(String(e));
      setProjects([]);
      setSelectedProject(null);
    } finally {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.timeEnd('client-project-load');
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return {
    projects,
    selectedProject,
    projectId: selectedProject?.id ?? null,
    projectCode: selectedProject?.project_code ?? null,
    loading,
    error,
    selectProject: setSelectedProject,
  };
}
